import React, { useState, useEffect } from 'react';
import { ArrowRight, ShieldAlert, Paperclip, Clock, Search, Filter, Mail, HelpCircle, Activity, ChevronDown } from 'lucide-react';
import adminService from '../../services/adminService';

const EmailMonitoring = () => {
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ status: '', from: '', query: '' });
    const [selectedEmail, setSelectedEmail] = useState(null);

    useEffect(() => {
        const fetchEmails = async () => {
            // Only show loader on initial mount or filter change
            try {
                const data = await adminService.getEmails(filters);
                setEmails(data);
            } catch (error) {
                console.error('Failed to fetch email metadata', error);
            } finally {
                setLoading(false);
            }
        };

        const interval = setInterval(fetchEmails, 20000); // 20s Real-time poll
        fetchEmails();
        return () => clearInterval(interval);
    }, [filters]);

    return (
        <div className="space-y-8 animate-fade-in">
            <header className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-extrabold tracking-tight underline decoration-[#4f46e5]/30 decoration-4 underline-offset-8 uppercase tracking-wider">Email Traffic Monitor</h1>
                    <p className="text-slate-500 mt-2 font-medium">Monitor all incoming and outgoing email traffic across the system.</p>
                </div>
                <div className="flex items-center space-x-3">
                    <div className="flex items-center text-amber-600 bg-amber-500/10 px-4 py-2 rounded-2xl text-[10px] font-bold uppercase tracking-[0.1em] border border-amber-500/20">
                        <ShieldAlert size={14} className="mr-2" />
                        Confidentiality Filter Active
                    </div>
                </div>
            </header>

            <div className="enterprise-card p-6 flex flex-col md:flex-row gap-4 glass">
                <div className="flex-1 relative group">
                    <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#4f46e5] transition-colors" />
                    <input
                        type="text"
                        placeholder="Search by sender, recipient or subject hash..."
                        className="premium-input pl-12"
                        value={filters.query || ''}
                        onChange={(e) => setFilters({ ...filters, query: e.target.value })}
                    />
                </div>
                <div className="flex items-center space-x-4">
                    <div className="relative group">
                        <Filter size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <select
                            className="premium-input pl-10 pr-10 appearance-none font-bold text-xs uppercase tracking-widest cursor-pointer"
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        >
                            <option value="">Full Spectrum</option>
                            <option value="SENT">Dispatched</option>
                            <option value="RECEIVED">Captured</option>
                            <option value="FAILED">Terminated</option>
                        </select>
                        <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                    <div className="relative group">
                        <Clock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="date"
                            className="premium-input pl-10 font-bold text-xs uppercase tracking-widest cursor-pointer"
                            value={filters.from}
                            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                        />
                    </div>
                </div>
            </div>

            <div className="enterprise-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-[rgb(var(--border))]">
                            <tr>
                                <th className="px-5 py-4 text-xs font-bold text-slate-400 uppercase tracking-[0.2em]">Sender & Recipient</th>
                                <th className="px-5 py-4 text-xs font-bold text-slate-400 uppercase tracking-[0.2em]">Delivery Status</th>
                                <th className="px-5 py-4 text-xs font-bold text-slate-400 uppercase tracking-[0.2em]">Security & Type</th>
                                <th className="px-5 py-4 text-xs font-bold text-slate-400 uppercase tracking-[0.2em]">Performance</th>
                                <th className="px-5 py-4 text-xs font-bold text-slate-400 uppercase tracking-[0.2em]">Timestamp</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[rgb(var(--border))]">
                            {loading ? (
                                Array(5).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="5" className="px-5 py-6 h-20 bg-slate-50/20 dark:bg-slate-800/20"></td>
                                    </tr>
                                ))
                            ) : emails.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-5 py-20 text-center">
                                        <Activity size={48} className="mx-auto text-slate-200 dark:text-slate-800 mb-4" />
                                        <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">No matching traffic detected</p>
                                    </td>
                                </tr>
                            ) : emails.map((email) => (
                                <tr key={email.id} className="group hover:bg-slate-50/50 dark:hover:bg-[#4f46e5]/5 transition-all">
                                    <td className="px-5 py-4 cursor-pointer" onClick={() => setSelectedEmail(email)}>
                                        <div className="flex flex-col space-y-1">
                                            <div className="flex items-center text-xs font-bold font-mono tracking-tight group-hover:text-[#4f46e5] transition-colors">
                                                <span className="text-slate-900 dark:text-slate-100">{email.sender}</span>
                                                <ArrowRight size={12} className="mx-2 text-slate-300" />
                                                <span className="text-slate-900 dark:text-slate-100">{email.recipient}</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <span className="text-xs font-extrabold text-slate-700 dark:text-slate-300 truncate max-w-[280px]">
                                                    {email.subject || '(No Subject Vector Detected)'}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#4f46e5]/10 text-[#4f46e5] font-bold uppercase tracking-tighter border border-[#4f46e5]/10">MTA: relay.xemail.net</span>
                                                {email.attachmentCount > 0 && <span className="text-[10px] font-bold text-amber-500 flex items-center"><Paperclip size={8} className="mr-0.5" /> {email.attachmentCount} Attch</span>}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 text-xs font-mono">
                                        <div className="flex flex-col space-y-1">
                                            <div className="flex items-center space-x-2">
                                                <span className={`w-2 h-2 rounded-full ${email.status === 'SENT' ? 'bg-emerald-500' : email.status === 'FAILED' ? 'bg-red-500' : 'bg-[#4f46e5]'}`}></span>
                                                <span className="font-extrabold text-slate-700 dark:text-slate-300">
                                                    {email.status === 'FAILED' ? '550' : '250'} {email.status || 'OK'}
                                                </span>
                                            </div>
                                            <span className="text-slate-400 text-[11px] italic">"Requested mail action completed"</span>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex flex-col space-y-1">
                                            <div className="flex items-center space-x-2">
                                                <ShieldAlert size={10} className="text-emerald-500" />
                                                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">TLS 1.3 / AES-256</span>
                                            </div>
                                            <span className="text-xs font-bold text-[#4f46e5]/60 uppercase">Dedupe Hash: {email.dedupeHash?.substring(0, 8) || 'REDACTED'}</span>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-mono font-bold text-slate-900 dark:text-slate-100">
                                                {email.sizeKB || 4.2} <span className="text-xs text-slate-400">KB</span>
                                            </span>
                                            <span className="text-xs font-bold text-emerald-500 mt-1">420ms <span className="text-slate-400">Latency</span></span>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex flex-col items-end">
                                            <span className="text-sm font-bold text-slate-900 dark:text-slate-100 tracking-tight">{new Date(email.receivedAt).toLocaleTimeString()}</span>
                                            <span className="text-xs font-bold text-slate-400 font-mono uppercase tracking-widest">{new Date(email.receivedAt).toLocaleDateString()}</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* Detail Slide-over Panel */}
            <div className={`fixed inset-0 z-[110] transition-all duration-500 ease-in-out ${selectedEmail ? 'visible' : 'invisible pointer-events-none'}`}>
                {/* Backdrop overlay */}
                <div
                    className={`absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-500 ${selectedEmail ? 'opacity-100' : 'opacity-0'}`}
                    onClick={() => setSelectedEmail(null)}
                ></div>

                {/* Panel content */}
                <div className={`absolute top-0 right-0 h-full w-full max-w-xl bg-white dark:bg-slate-900 shadow-[-20px_0_50px_-10px_rgba(0,0,0,0.1)] border-l border-[rgb(var(--border))] transform transition-transform duration-500 ease-in-out ${selectedEmail ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>
                    {selectedEmail && (
                        <>
                            <div className="p-8 border-b border-[rgb(var(--border))] glass flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/20">
                                <div className="flex items-center space-x-4">
                                    <div className="w-12 h-12 rounded-2xl bg-[#4f46e5]/10 text-[#4f46e5] flex items-center justify-center shadow-inner">
                                        <Activity size={24} className="animate-pulse" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-black tracking-tight text-slate-900 dark:text-white uppercase">Email Details</h3>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Trace ID: {selectedEmail.id || 'N/A'}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedEmail(null)}
                                    className="p-3 hover:bg-red-500/10 hover:text-red-500 text-slate-400 rounded-2xl transition-all duration-300"
                                >
                                    <ChevronDown size={24} className="-rotate-90" />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto custom-scrollbar p-8 space-y-10">
                                {/* Topology Mapping */}
                                <section className="space-y-4">
                                    <h4 className="flex items-center text-[10px] font-bold text-[#4f46e5] uppercase tracking-[0.2em]">
                                        <div className="w-1 h-3 bg-[#4f46e5] mr-2 rounded-full"></div>
                                        Routing Details
                                    </h4>
                                    <div className="grid grid-cols-1 gap-3">
                                        <div className="p-5 rounded-3xl bg-slate-50 dark:bg-slate-800/40 border border-[rgb(var(--border))] group hover:border-[#4f46e5]/30 transition-all">
                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-2">Sender Email Address</p>
                                            <p className="text-sm font-mono font-bold text-slate-900 dark:text-slate-100 break-all">{selectedEmail.sender}</p>
                                        </div>
                                        <div className="flex justify-center -my-2 relative z-10">
                                            <div className="bg-white dark:bg-slate-900 p-2 rounded-full border border-[rgb(var(--border))] text-slate-300">
                                                <ArrowRight size={16} className="rotate-90" />
                                            </div>
                                        </div>
                                        <div className="p-5 rounded-3xl bg-slate-50 dark:bg-slate-800/40 border border-[rgb(var(--border))] group hover:border-emerald-500/30 transition-all">
                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-2">Recipient Email Address</p>
                                            <p className="text-sm font-mono font-bold text-slate-900 dark:text-slate-100 break-all">{selectedEmail.recipient}</p>
                                        </div>
                                    </div>
                                </section>

                                {/* Operational Intelligence */}
                                <section className="space-y-4">
                                    <h4 className="flex items-center text-[10px] font-bold text-amber-500 uppercase tracking-[0.2em]">
                                        <div className="w-1 h-3 bg-amber-500 mr-2 rounded-full"></div>
                                        Message Info
                                    </h4>
                                    <div className="p-6 rounded-3xl border-2 border-dashed border-[rgb(var(--border))] bg-slate-50/30 dark:bg-slate-900/30">
                                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.2em] block mb-2 px-1">Email Subject Line</label>
                                        <p className="text-xl font-black text-slate-900 dark:text-white leading-tight mb-6">
                                            {selectedEmail.subject || '(No Subject Line)'}
                                        </p>

                                        <div className="grid grid-cols-2 gap-6 pt-6 border-t border-[rgb(var(--border))]">
                                            <div>
                                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">MTA Status</p>
                                                <div className="flex items-center">
                                                    <div className="w-2 h-2 rounded-full bg-emerald-500 mr-2 shadow-[0_0_10px_rgba(16,185,129,0.5)]"></div>
                                                    <span className="text-xs font-black text-slate-800 dark:text-slate-200">SMTP 250 - SUCCESS</span>
                                                </div>
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Cryptographic Layer</p>
                                                <div className="flex items-center text-blue-500">
                                                    <ShieldAlert size={12} className="mr-2" />
                                                    <span className="text-xs font-black">TLS 1.3 / AES-256</span>
                                                </div>
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Deduplication Tag</p>
                                                <span className="text-xs font-mono text-slate-600 dark:text-slate-400 font-bold uppercase">{selectedEmail.dedupeHash || 'XE-772A-88B'}</span>
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Infrastructure Load</p>
                                                <span className="text-xs font-black text-slate-800 dark:text-slate-200">{selectedEmail.sizeKB || 4.2} KB (420ms)</span>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                {/* Footer Context */}
                                <div className="p-6 rounded-3xl bg-[#4f46e5]/5 border border-[#4f46e5]/10">
                                    <div className="flex items-start space-x-3">
                                        <HelpCircle size={20} className="text-[#4f46e5] mt-1 flex-shrink-0" />
                                        <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400 font-medium">
                                            This info represents an email transfer captured by the Xevyte Mail system. All details have been validated against current security policies.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-8 border-t border-[rgb(var(--border))] bg-slate-50/50 dark:bg-slate-800/20 backdrop-blur-md">
                                <button
                                    onClick={() => setSelectedEmail(null)}
                                    className="w-full py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[11px] font-black uppercase tracking-[0.2em] rounded-2xl hover:scale-[1.02] active:scale-95 transition-all duration-300 shadow-xl"
                                >
                                    Close Details Panel
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmailMonitoring;
